import { createForm } from '@leprecoins/effector-forms';
import { sample } from 'effector';
import { equals, not, spread } from 'patronum';
import { createBlockAccountModel } from 'features/block-account';
import { createDeleteAccountModel } from 'features/delete-account';
import { createUnblockAccountModel } from 'features/unblock-account';
import {
	ACCOUNT_NAME_MIN_LENGTH,
	ACCOUNT_NOTE_MAX_LENGTH,
	accountTreeModel,
} from 'entities/account';
import { viewerModel } from 'entities/viewer';
import { editAccountMutation } from 'shared/api';
import { notEmptyRule, minLengthRule, maxLengthRule, notify } from 'shared/lib';
import { accountPageModel } from './account-page-model';

const blockAccountModel = createBlockAccountModel();

const unblockAccountModel = createUnblockAccountModel();

const deleteAccountModel = createDeleteAccountModel();

const form = createForm({
	fields: {
		name: {
			init: '',
			rules: [notEmptyRule, minLengthRule(ACCOUNT_NAME_MIN_LENGTH)],
		},
		note: {
			init: '',
			rules: [maxLengthRule(ACCOUNT_NOTE_MAX_LENGTH)],
		},
		canCreateSubAccounts: {
			init: false,
		},
		canWriteUsers: {
			init: false,
		},
		canEditLimits: {
			init: false,
		},
		canEditGameSettings: {
			init: false,
		},
		canWriteCashdesks: {
			init: false,
		},
		canBlockGames: {
			init: false,
		},
	},
	validateOn: ['submit'],
	resetErrorsOnChange: true,
});

const { $pending } = editAccountMutation;

const $isAttachedAccount = equals(
	viewerModel.$accountId,
	accountPageModel.$account.map((account) => account?.id || -1),
);

sample({
	clock: accountPageModel.getAccountQuery.finished.success,
	source: viewerModel.$permissions,
	filter: not($isAttachedAccount),
	target: spread({
		canCreateSubAccounts: form.fields.canCreateSubAccounts.setActive,
		canWriteUsers: form.fields.canWriteUsers.setActive,
		canEditLimits: form.fields.canEditLimits.setActive,
		canEditGameSettings: form.fields.canEditGameSettings.setActive,
		canWriteCashdesks: form.fields.canWriteCashdesks.setActive,
		canBlockGames: form.fields.canBlockGames.setActive,
	}),
});

sample({
	clock: accountPageModel.getAccountQuery.finished.success,
	filter: $isAttachedAccount,
	target: [
		form.fields.canCreateSubAccounts.disable,
		form.fields.canWriteUsers.disable,
		form.fields.canEditLimits.disable,
		form.fields.canEditGameSettings.disable,
		form.fields.canWriteCashdesks.disable,
		form.fields.canBlockGames.disable,
	],
});

sample({
	clock: accountPageModel.getAccountQuery.finished.success,
	fn: ({ result }) => ({
		...result,
		...result.permissions,
		note: result.note ?? '',
	}),
	target: [form.setValues, form.setInitialValues],
});

sample({
	clock: form.submitted,
	source: {
		values: form.$values,
		id: accountPageModel.$accountId,
	},
	filter: ({ id }) => id !== null,
	fn: ({ values: { name, note, ...permissions }, id }) => ({
		id: id!,
		note,
		name,
		permissions: {
			...permissions,
		},
	}),
	target: editAccountMutation.start,
});

sample({
	clock: [editAccountMutation.finished.success, deleteAccountModel.mutation.finished.success],
	target: accountTreeModel.fetch,
});

notify(editAccountMutation.finished.success, 'edit-account.success');

sample({
	clock: [
		editAccountMutation.finished.success,
		blockAccountModel.mutation.finished.success,
		unblockAccountModel.mutation.finished.success,
	],
	source: accountPageModel.$accountId,
	filter: Boolean,
	target: accountPageModel.getAccountQuery.start,
});

export const accountPageSettingsModel = {
	blockAccountModel,
	unblockAccountModel,
	deleteAccountModel,
	form,
	$pending,
};
